import React, { useState, useEffect } from 'react';
import { Card, Table, Switch, Button, Input, Space, message, Typography, Form, Tag, Modal } from 'antd';
import { LockOutlined, CheckOutlined, CloseOutlined, ReloadOutlined, PlusOutlined, KeyOutlined, LogoutOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Title, Text } = Typography;

// 页面访问授权
const OPS_AUTH_KEY = 'ops_whitelist_authed';

const ProviderWhitelist = () => {
  const [authenticated, setAuthenticated] = useState(() => {
    return sessionStorage.getItem(OPS_AUTH_KEY) === 'true';
  });
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);

  // 添加供应商表单
  const [addFormVisible, setAddFormVisible] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm] = Form.useForm();

  // 修改密码弹窗
  const [pwdModalVisible, setPwdModalVisible] = useState(false);
  const [pwdChanging, setPwdChanging] = useState(false);
  const [pwdForm] = Form.useForm();

  useEffect(() => {
    if (authenticated) {
      fetchProviders();
    }
  }, [authenticated]);

  const handleLogin = async () => {
    if (!password.trim()) {
      message.warning('请输入管理密码');
      return;
    }
    setAuthLoading(true);
    try {
      const res = await adminAPI.verifyOpsPassword(password.trim());
      if (res.code === 0) {
        setAuthenticated(true);
        sessionStorage.setItem(OPS_AUTH_KEY, 'true');
        message.success('验证通过');
      } else {
        message.error(res.message || '密码错误');
      }
    } catch (e) {
      message.error('验证失败：' + (e.message || '网络错误'));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(OPS_AUTH_KEY);
    setAuthenticated(false);
    setPassword('');
    message.info('已退出');
  };

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getProviderWhitelist();
      if (res.code === 0) {
        setProviders(res.data.list || []);
      } else {
        message.error(res.message || '获取失败');
      }
    } catch (e) {
      message.error('获取数据列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id, checked) => {
    try {
      const res = await adminAPI.toggleProviderApproval(id);
      if (res.code === 0) {
        message.success(checked ? '已启用' : '已禁用');
        // 更新本地列表
        setProviders(prev =>
          prev.map(p => (p.id === id ? { ...p, is_approved: !p.is_approved } : p))
        );
      } else {
        message.error(res.message || '操作失败');
      }
    } catch (e) {
      message.error('操作失败');
    }
  };

  const handleAddProvider = async (values) => {
    setAdding(true);
    try {
      const res = await adminAPI.addWhitelistProvider(values);
      if (res.code === 0) {
        message.success('配置已添加');
        addForm.resetFields();
        setAddFormVisible(false);
        fetchProviders();
      } else {
        message.error(res.message || '添加失败');
      }
    } catch (e) {
      message.error('添加失败: ' + (e.message || '网络错误'));
    } finally {
      setAdding(false);
    }
  };

  const handleChangePassword = async (values) => {
    setPwdChanging(true);
    try {
      const res = await adminAPI.changeOpsPassword(values.old_password, values.new_password);
      if (res.code === 0) {
        message.success('密码修改成功');
        pwdForm.resetFields();
        setPwdModalVisible(false);
      } else {
        message.error(res.message || '修改失败');
      }
    } catch (e) {
      message.error('修改失败: ' + (e.message || '网络错误'));
    } finally {
      setPwdChanging(false);
    }
  };

  // 密码登录界面
  if (!authenticated) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}>
        <Card style={{ width: 400, textAlign: 'center' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <LockOutlined style={{ fontSize: 48, color: '#1890ff' }} />
              <Title level={4} style={{ marginTop: 16 }}>系统管理</Title>
              <Text type="secondary">请输入管理密码以访问</Text>
            </div>
            <Input.Password
              size="large"
              placeholder="管理密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onPressEnter={handleLogin}
            />
            <Button
              type="primary"
              size="large"
              block
              loading={authLoading}
              onClick={handleLogin}
            >
              验证登录
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  // 白名单管理界面
  const approvedCount = providers.filter(p => p.is_approved).length;
  const unapprovedCount = providers.filter(p => !p.is_approved).length;

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '名称', dataIndex: 'name', width: 130 },
    { title: '显示名称', dataIndex: 'display_name', width: 150 },
    { title: '地址', dataIndex: 'api_base', ellipsis: true },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (s) => <Tag color={s === 'active' ? 'green' : 'red'}>{s === 'active' ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '授权接入', dataIndex: 'is_approved', width: 100,
      render: (approved, record) => (
        <Switch
          checked={approved}
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<CloseOutlined />}
          onChange={(checked) => handleToggle(record.id, checked)}
        />
      ),
    },
    {
      title: '结果', dataIndex: 'is_approved', width: 100,
      render: (approved) => (
        <Tag color={approved ? 'success' : 'error'}>
          {approved ? '已启用' : '未启用'}
        </Tag>
      ),
    },
    { title: '创建时间', dataIndex: 'created_at', width: 170 },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <LockOutlined style={{ fontSize: 20, color: '#1890ff' }} />
          <Title level={4} style={{ margin: 0 }}>系统配置管理</Title>
          <Tag color="blue">已批准 {approvedCount}</Tag>
          {unapprovedCount > 0 && <Tag color="orange">待审批 {unapprovedCount}</Tag>}
        </Space>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddFormVisible(true)}>
            添加供应商
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchProviders}>刷新</Button>
          <Button icon={<KeyOutlined />} onClick={() => { pwdForm.resetFields(); setPwdModalVisible(true); }}>
            修改密码
          </Button>
          <Button danger icon={<LogoutOutlined />} onClick={handleLogout}>退出</Button>
        </Space>
      </div>

      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        开关控制是否批准启用。用户自行添加的条目默认不批准，不会实际生效。
      </Text>

      {/* 添加供应商表单 */}
      {addFormVisible && (
        <Card title="添加供应商" size="small" style={{ marginBottom: 16 }}>
          <Form form={addForm} layout="inline" onFinish={handleAddProvider}>
            <Form.Item name="name" label="名称" rules={[{ required: true, message: '必填' }]}>
              <Input placeholder="deepseek" style={{ width: 130 }} />
            </Form.Item>
            <Form.Item name="display_name" label="显示名" rules={[{ required: true }]}>
              <Input placeholder="DeepSeek" style={{ width: 140 }} />
            </Form.Item>
            <Form.Item name="api_base" label="地址" rules={[{ required: true }]}>
              <Input placeholder="https://api.deepseek.com" style={{ width: 240 }} />
            </Form.Item>
            <Form.Item name="api_key" label="密钥" rules={[{ required: true }]}>
              <Input placeholder="sk-xxx" style={{ width: 200 }} />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={adding}>添加</Button>
                <Button onClick={() => setAddFormVisible(false)}>取消</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      )}

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={pwdModalVisible}
        onCancel={() => setPwdModalVisible(false)}
        okText="确认修改"
        cancelText="取消"
        confirmLoading={pwdChanging}
        onOk={() => pwdForm.submit()}
      >
        <Form form={pwdForm} layout="vertical" onFinish={handleChangePassword} style={{ marginTop: 16 }}>
          <Form.Item name="old_password" label="旧密码" rules={[{ required: true, message: '请输入旧密码' }]}>
            <Input.Password placeholder="当前密码" />
          </Form.Item>
          <Form.Item name="new_password" label="新密码" rules={[
            { required: true, message: '请输入新密码' },
            { min: 6, message: '新密码不能少于6位' },
          ]}>
            <Input.Password placeholder="新密码（至少6位）" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>

      <Table
        columns={columns}
        dataSource={providers}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 20, showTotal: (total) => `共 ${total} 条记录` }}
      />
    </div>
  );
};

export default ProviderWhitelist;
